/**
 * DR Gifts Members Club (Dawn)
 * Keeps members coupons logic isolated from the gift reconciler.
 */
(function () {
  'use strict';

  if (window.__drGiftsMembersMounted) return;
  window.__drGiftsMembersMounted = true;

  const MEMBERS_UI_STATE_KEY = 'drGifts.membersUiState';
  const MEMBERS_RERENDER_EVENT = 'dr:members-rerendered';
  const DEFAULT_TIMEOUT = 12000;
  const MIN_LOADING_MS = 260;
  const INAPPLICABLE_TOOLTIP_HIDE_MS = 5000;
  const COUPON_NOT_COMBINED_TEXT = 'הקופון לא משתלב עם ההנחה שכבר בסל';
  let refreshInFlight = false;
  let refreshQueued = false;
  let refreshEpoch = 0;
  let lastSeenRevision = 0;
  let hydratedFor = null;
  let latestCartSnapshot = null;
  let latestCartRevision = 0;
  let forceRefreshPending = false;
  let drawerObserver = null;
  let observedDrawer = null;
  let debugRootCounter = 0;
  let creatableActionQueue = Promise.resolve();
  let creatableActionInFlight = false;
  let inapplicableTooltipTimer = null;

  function isDebugEnabled() {
    try {
      return new URLSearchParams(window.location.search).get('dr_members_debug') === '1' ||
        window.localStorage?.getItem('drMembersDebug') === '1';
    } catch (_) {
      return false;
    }
  }

  function pushDebug(event, details = {}) {
    if (!isDebugEnabled()) return;
    const entry = {
      ts: new Date().toISOString(),
      t: Math.round(performance.now()),
      event,
      ...details,
    };
    window.drMembersDebugLog = Array.isArray(window.drMembersDebugLog) ? window.drMembersDebugLog : [];
    window.drMembersDebugLog.push(entry);
    if (window.drMembersDebugLog.length > 400) window.drMembersDebugLog.shift();
    console.debug('[DR Members Debug]', event, entry);
  }

  function getRootDebugId(root) {
    if (!root) return null;
    if (!root.dataset.drMembersDebugRootId) {
      if (!isDebugEnabled()) return null;
      debugRootCounter += 1;
      root.dataset.drMembersDebugRootId = 'members-root-' + debugRootCounter;
    }
    return root.dataset.drMembersDebugRootId;
  }

  function getVisibleStateKey(root) {
    if (!root) return null;
    const state = Array.from(root.querySelectorAll('[data-dr-members-state]'))
      .find((el) => !el.hasAttribute('hidden'));
    return state?.getAttribute('data-dr-members-state') || null;
  }

  function summarizeRoot(root, rootState = null) {
    if (!root) return { rootId: null, rootConnected: false };
    const state = rootState || readRootState(root, latestCartSnapshot);
    return {
      rootId: getRootDebugId(root),
      rootConnected: root.isConnected,
      visibleState: getVisibleStateKey(root),
      loggedIn: !!state.loggedIn,
      hasCustomerId: !!state.customerId,
      hasCustomerEmail: !!state.customerEmail,
      mainActive: !!state.mainActive,
      cartTotalAgorot: Number(state.cartTotalAgorot || 0) || 0,
    };
  }

  function isCurrentRoot(root) {
    return !!root && getMembersRoot() === root;
  }

  function scheduleStaleRootRefresh(reason, epoch, root) {
    pushDebug('members-refresh-stale-root-reschedule', summarizeRefresh({
      epoch,
      reason,
      rootStillCurrent: isCurrentRoot(root),
      ...summarizeRoot(root),
    }));
    setTimeout(() => {
      if (readUiState().activeTab === 'members') {
        requestRefresh({ force: true });
      }
    }, 0);
  }

  function summarizeRefresh(extra = {}) {
    return {
      refreshEpoch,
      refreshInFlight,
      refreshQueued,
      forceRefreshPending,
      lastSeenRevision,
      hydratedRevision: hydratedFor?.revision ?? -1,
      latestCartRevision,
      settlementIdle: isSettlementIdle(),
      ...extra,
    };
  }

  function getGiftsConfig() {
    return window.DR_GIFTS_CONFIG || null;
  }

  function getUiBridge() {
    return window.drCartGiftsUi || null;
  }

  function getCartStore() {
    return window.drCartStore || null;
  }

  function getTransport() {
    return window.drShopifyCartTransport || null;
  }

  function getSettlementCoordinator() {
    return window.drCartSettlementCoordinator || null;
  }

  function getModules() {
    return getGiftsConfig()?.modules || {};
  }

  function isModuleEnabled(key) {
    const modules = getModules();
    return modules[key] !== false;
  }

  function getAvailableTabs() {
    const uiBridge = getUiBridge();
    if (uiBridge?.getAvailableTabs) {
      return uiBridge.getAvailableTabs(getGiftsConfig());
    }
    const cfg = getGiftsConfig() || {};
    const tabs = [];
    const giftsEnabled =
      (isModuleEnabled('free') && Array.isArray(cfg.free) && cfg.free.length > 0) ||
      (isModuleEnabled('bonus1') && Array.isArray(cfg.bonus?.variants) && cfg.bonus.variants.length > 0) ||
      (isModuleEnabled('bonus2') && Array.isArray(cfg.bonus2?.variants) && cfg.bonus2.variants.length > 0) ||
      (isModuleEnabled('bonus3') && Array.isArray(cfg.bonus3?.variants) && cfg.bonus3.variants.length > 0);
    if (giftsEnabled) tabs.push('gifts');
    if (isModuleEnabled('membersTab')) tabs.push('members');
    if (isModuleEnabled('newMemberTab')) tabs.push('new-customer');
    if (!tabs.length) tabs.push('gifts');
    return tabs;
  }

  function getReconciler() {
    return window.drGiftsReconciler || null;
  }

  function normalizeTab(tab) {
    const uiBridge = getUiBridge();
    if (uiBridge?.normalizeTab) {
      return uiBridge.normalizeTab(tab, getGiftsConfig());
    }
    const value = String(tab || '').trim();
    const tabs = getAvailableTabs();
    if ((value === 'members' || value === 'new-customer' || value === 'gifts') && tabs.includes(value)) {
      return value;
    }
    return tabs[0];
  }

  function readUiState() {
    const reconciler = getReconciler();
    if (reconciler?.readMembersUiState) {
      const state = reconciler.readMembersUiState();
      if (state) return state;
    }
    const uiBridge = getUiBridge();
    if (uiBridge?.readMembersUiState) {
      return uiBridge.readMembersUiState(getGiftsConfig()) || { activeTab: 'gifts', membersOpen: false, returnPending: false };
    }
    try {
      const raw = sessionStorage.getItem(MEMBERS_UI_STATE_KEY);
      if (!raw) return { activeTab: 'gifts', membersOpen: false, returnPending: false };
      const parsed = JSON.parse(raw);
      return {
        activeTab: normalizeTab(parsed?.activeTab),
        membersOpen: parsed?.membersOpen === true,
        returnPending: parsed?.returnPending === true,
      };
    } catch (_) {
      return { activeTab: 'gifts', membersOpen: false, returnPending: false };
    }
  }

  function writeUiState(patch) {
    const reconciler = getReconciler();
    if (reconciler?.writeMembersUiState) return reconciler.writeMembersUiState(patch);
    const uiBridge = getUiBridge();
    if (uiBridge?.writeMembersUiState) {
      return uiBridge.writeMembersUiState(patch, getGiftsConfig());
    }
    const current = readUiState();
    const next = {
      activeTab: typeof patch?.activeTab !== 'undefined' ? normalizeTab(patch.activeTab) : current.activeTab,
      membersOpen: typeof patch?.membersOpen === 'boolean' ? patch.membersOpen : current.membersOpen,
      returnPending: typeof patch?.returnPending === 'boolean' ? patch.returnPending : current.returnPending,
    };
    try {
      sessionStorage.setItem(MEMBERS_UI_STATE_KEY, JSON.stringify(next));
    } catch (_) {}
    return next;
  }

  function getConfig() {
    const cfg = window.DR_GIFTS_MEMBERS;
    if (!cfg) return null;
    return {
      optionsEndpoint: String(cfg.optionsEndpoint || '/apps/drnew/members/options'),
      redeemEndpoint: String(cfg.redeemEndpoint || '/apps/drnew/members/redeem'),
      minPointsForCoupon: Number(cfg.minPointsForCoupon),
      pointsRatio: Number(cfg.pointsRatio) || 1,
      timeoutMs: Number(cfg.timeoutMs) || DEFAULT_TIMEOUT,
      loginUrl: String(cfg.loginUrl || '/customer_authentication/login'),
    };
  }

  function getWidget() {
    const uiBridge = getUiBridge();
    if (uiBridge?.getWidget) {
      return uiBridge.getWidget();
    }
    return document.querySelector('cart-drawer .dr-gifts[data-dr-gifts-widget], .dr-gifts[data-dr-gifts-widget]');
  }

  function getMembersRoot() {
    return getWidget()?.querySelector('[data-dr-members]') || null;
  }

  function applyTabState(widget, activeTab) {
    const uiBridge = getUiBridge();
    if (uiBridge?.applyTabState) {
      return uiBridge.applyTabState(widget, activeTab, getGiftsConfig());
    }
    if (!widget) return;
    const normalizedTab = normalizeTab(activeTab);
    widget.setAttribute('data-dr-active-tab', normalizedTab);
    widget.querySelectorAll('[data-dr-tab]').forEach((tabButton) => {
      const isActive = tabButton.getAttribute('data-dr-tab') === normalizedTab;
      tabButton.classList.toggle('dr-gifts__tab--active', isActive);
      tabButton.setAttribute('aria-selected', String(isActive));
      tabButton.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    widget.querySelectorAll('[data-dr-panel]').forEach((panel) => {
      const isActive = panel.getAttribute('data-dr-panel') === normalizedTab;
      panel.toggleAttribute('hidden', !isActive);
    });
    if (normalizedTab !== 'gifts') widget.classList.remove('is-picker-open');
  }

  function setActiveTab(activeTab, opts = {}) {
    const widget = getWidget();
    if (!widget) return;
    const normalizedTab = normalizeTab(activeTab);
    applyTabState(widget, normalizedTab);
    if (opts.persist !== false) {
      const nextState = {
        activeTab: normalizedTab,
      };
      if (typeof opts.membersOpen === 'boolean') nextState.membersOpen = opts.membersOpen;
      writeUiState(nextState);
    }
  }

  function getStateElements(root) {
    return {
      loading: root.querySelector('[data-dr-members-state="loading"]'),
      loggedOut: root.querySelector('[data-dr-members-state="logged-out"]'),
      activateMain: root.querySelector('[data-dr-members-state="activate-main"]'),
      eligible: root.querySelector('[data-dr-members-state="eligible"]'),
      points: root.querySelector('[data-dr-members-points]'),
      existingWrap: root.querySelector('[data-dr-members-existing-wrap]'),
      creatableWrap: root.querySelector('[data-dr-members-creatable-wrap]'),
      minPointsMessage: root.querySelector('[data-dr-members-min-points-message]'),
      minPointsValue: root.querySelector('[data-dr-members-min-points-value]'),
      existingSelect: root.querySelector('[data-dr-members-existing-select]'),
      creatableSelect: root.querySelector('[data-dr-members-creatable-select]'),
      applyExisting: root.querySelector('[data-dr-members-apply-existing]'),
      applyCreatable: root.querySelector('[data-dr-members-apply-creatable]'),
      inapplicableTooltip: root.querySelector('[data-dr-members-inapplicable-tooltip]'),
    };
  }

  function normalizeMinPoints(value) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return Math.floor(parsed);
    return 800;
  }

  function syncMinPointsMessage(els, minPoints, visible) {
    if (!els) return;
    const normalizedMinPoints = normalizeMinPoints(minPoints);
    if (els.minPointsValue) els.minPointsValue.textContent = String(normalizedMinPoints);
    if (els.minPointsMessage) {
      if (visible) els.minPointsMessage.removeAttribute('hidden');
      else els.minPointsMessage.setAttribute('hidden', '');
    }
  }

  function hideAllStates(els) {
    [els.loading, els.loggedOut, els.activateMain, els.eligible].forEach((el) => el?.setAttribute('hidden', ''));
  }

  function showState(els, key) {
    hideAllStates(els);
    els[key]?.removeAttribute('hidden');
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function ensureMinLoadingWindow(startedAt, epoch) {
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_LOADING_MS) {
      await wait(MIN_LOADING_MS - elapsed);
    }
    return epoch === refreshEpoch;
  }

  function primeMembersLoadingState() {
    const root = getMembersRoot();
    if (!root) return;
    const rootState = readRootState(root, latestCartSnapshot);
    if (!rootState.loggedIn || (!rootState.mainActive && !rootState.exclusiveActive)) return;
    showState(getStateElements(root), 'loading');
  }

  function parseNonNegativeInt(...values) {
    for (const value of values) {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed >= 0) return Math.floor(parsed);
    }
    return null;
  }

  function fillSelect(selectEl, items, valueKey, defaultText, opts = {}) {
    if (!selectEl) return;
    selectEl.innerHTML = '';
    
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = defaultText;
    selectEl.appendChild(defaultOption);

    items.forEach((item) => {
      const option = document.createElement('option');
      option.value = String(item[valueKey] || '');
      option.textContent = item.label;
      if (opts.disableNonApplicable && item.applicable === false) {
        option.disabled = true;
        const minCartAgorot = Number(item?.minCartAgorot);
        const hasThreshold = Number.isFinite(minCartAgorot) && minCartAgorot > 0;
        const thresholdToken = 'זמין מ-₪';
        if (hasThreshold && !String(option.textContent || '').includes(thresholdToken)) {
          const minIls = Math.ceil(minCartAgorot / 100);
          option.textContent = String(item.label || '') + ' (' + thresholdToken + minIls + ')';
        }
      }
      selectEl.appendChild(option);
    });
  }

  function normalizeExistingCoupons(list) {
    function readDiscountText(item) {
      const text = String(
        item?.discount_text ?? item?.discountText ?? item?.action ?? item?.action_name ?? ''
      ).trim();
      if (!text) return null;
      return text
        .replace(/\s*הנחה\s*/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim() || null;
    }

    function readDiscountNis(item) {
      const agorotRaw = item?.discountAgorot ?? item?.discount_agorot ?? item?.amountAgorot ?? item?.amount_agorot;
      const agorot = Number(agorotRaw);
      if (Number.isFinite(agorot) && agorot > 0) return agorot / 100;

      const raw = item?.discountNis ?? item?.discount_nis ?? item?.amountNis ?? item?.amount_nis ??
        item?.amount ?? item?.discountAmount ?? item?.discount_amount ?? item?.value ??
        item?.discount_text ?? item?.discountText ?? item?.benefit_text ?? item?.benefitText;
      const direct = Number(raw);
      if (Number.isFinite(direct) && direct > 0) return direct;

      const text = String(raw || '');
      const match = text.match(/(\d+(?:[.,]\d+)?)/);
      if (!match) return null;
      const parsed = Number(match[1].replace(/,/g, ''));
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return parsed;
    }

    function formatNis(value) {
      const hasFraction = Math.abs(value % 1) > 0.001;
      return hasFraction ? value.toFixed(2) : String(Math.round(value));
    }

    return (Array.isArray(list) ? list : [])
      .map((item) => {
        if (typeof item === 'string') {
          const code = item.trim().toUpperCase();
          return code ? { code, label: code, applicable: true, minCartAgorot: null } : null;
        }
        const code = String(item?.code || item?.couponCode || item?.reward_text || '').trim().toUpperCase();
        if (!code) return null;
        const baseLabel = String(item?.label || item?.name || code).trim() || code;
        const discountText = readDiscountText(item);
        const amountNis = readDiscountNis(item);
        const suffix = discountText || (amountNis ? ('₪' + formatNis(amountNis)) : '');
        const label = suffix ? (baseLabel + ' • ' + suffix) : baseLabel;
        const applicable = item?.applicable_now === false || item?.applicableNow === false ? false : true;
        const minCartAgorot = parseNonNegativeInt(item?.min_cart_agorot, item?.minCartAgorot);
        // Same order as the classic label suffix: the API's discount text, then a parsed amount.
        const amountText = discountText || (amountNis ? ('₪' + formatNis(amountNis)) : '');
        return { code, label, applicable, minCartAgorot, amountText };
      })
      .filter(Boolean);
  }

  function normalizeCreatableOptions(list) {
    return (Array.isArray(list) ? list : [])
      .map((item) => {
        const id = String(item?.id || item?.redemption_option_id || item?.optionId || '').trim();
        if (!id) return null;
        const applicable = item?.applicable_now === false || item?.applicableNow === false ? false : true;
        const minCartAgorot = parseNonNegativeInt(
          item?.min_cart_agorot,
          item?.minCartAgorot,
          item?.minimum_cart_agorot,
          item?.minimumCartAgorot,
          item?.required_cart_agorot,
          item?.requiredCartAgorot,
          item?.threshold_agorot,
          item?.thresholdAgorot
        );
        const thresholdToken = 'זמין מ-₪';
        const baseLabel = String(item?.label || item?.name || item?.cost_text || item?.costText || ('אפשרות ' + id)).trim();
        let label = baseLabel;
        if (
          Number.isFinite(minCartAgorot) &&
          minCartAgorot > 0 &&
          !String(baseLabel).includes(thresholdToken)
        ) {
          const minIls = Math.ceil(minCartAgorot / 100);
          label = baseLabel + ' (' + thresholdToken + minIls + ')';
        }
        return { id, label, baseLabel, minCartAgorot, applicable };
      })
      .filter(Boolean);
  }

  function setMembersPointsText(el, value) {
    if (!el) return;
    const numeric = Number(value);
    const points = Number.isFinite(numeric) && numeric >= 0 ? Math.floor(numeric) : 0;
    el.textContent = String(points) + " נק'";
  }

  function getConfiguredMainCouponCode() {
    return String(
      getGiftsConfig()?.couponCode ||
      window.DR_COUPON_REMINDER?.code ||
      ''
    ).trim().toUpperCase();
  }

  // Another percentage code Shopify applies instead of the sitewide one (an
  // earlier sitewide code such as ZOHAR, an influencer code) stays in the cart
  // and counts as the sitewide code: it opens the club. Club coupons are fixed
  // amounts, so they don't count.
  function hasOtherPercentageCode(cart, mainCode) {
    const isOther = (entry) => {
      const app = entry?.discount_application || entry;
      if (!app || (app.type && app.type !== 'discount_code') || app.value_type !== 'percentage') return false;
      const code = String(app.code || app.title || '').trim().toUpperCase();
      return !!code && code !== mainCode;
    };
    if ((cart?.cart_level_discount_applications || cart?.discount_applications || []).some(isOther)) return true;
    return (cart?.items || []).some((item) => (item?.line_level_discount_allocations || []).some(isOther));
  }

  function resolveMainCouponActive(root, cart) {
    const configuredCode = getConfiguredMainCouponCode();
    const candidateCart = cart || latestCartSnapshot || getCartStore()?.getSnapshot?.()?.cart || null;
    if (configuredCode && candidateCart) {
      return (
        extractActiveDiscountCodes(candidateCart).has(configuredCode) ||
        hasOtherPercentageCode(candidateCart, configuredCode)
      );
    }
    return root?.dataset?.mainActive === 'true';
  }

  // Codes that replace the sitewide one (DRNEW) open the club like it does, so
  // members aren't asked to swap them back. Whether a club coupon combines with
  // them is up to Shopify's discount combinations.
  function getExclusiveCouponCodes() {
    const codes = getGiftsConfig()?.coupon?.exclusiveCodes;
    return (Array.isArray(codes) && codes.length ? codes : ['DRNEW']).map((code) => String(code).trim().toUpperCase());
  }

  function resolveExclusiveCouponActive(root, cart) {
    const candidateCart = cart || latestCartSnapshot || getCartStore()?.getSnapshot?.()?.cart || null;
    if (candidateCart) {
      const active = extractActiveDiscountCodes(candidateCart);
      return getExclusiveCouponCodes().some((code) => active.has(code));
    }
    return root?.dataset?.exclusiveActive === 'true';
  }

  function syncRootCartState(root, cart) {
    if (!root || !cart) return;
    root.dataset.mainActive = resolveMainCouponActive(root, cart) ? 'true' : 'false';
    root.dataset.exclusiveActive = resolveExclusiveCouponActive(root, cart) ? 'true' : 'false';
    const totalPrice = Number(cart?.total_price);
    if (Number.isFinite(totalPrice) && totalPrice >= 0) {
      root.dataset.cartTotalAgorot = String(Math.floor(totalPrice));
    }
  }

  function readRootState(root, cart) {
    const customerId = String(root.dataset.customerId || '').trim();
    const customerEmail = String(root.dataset.customerEmail || '').trim();
    const loggedInFromDataset = root.dataset.loggedIn === 'true';
    return {
      loggedIn: loggedInFromDataset || Boolean(customerId || customerEmail),
      customerId,
      customerEmail,
      cartTotalAgorot: Number(root.dataset.cartTotalAgorot || 0) || 0,
      mainActive: resolveMainCouponActive(root, cart),
      exclusiveActive: resolveExclusiveCouponActive(root, cart),
      loginUrl: String(root.dataset.loginUrl || getConfig()?.loginUrl || '/customer_authentication/login'),
    };
  }

  function getSnapshotRevision(snapshot) {
    return Math.max(0, Number(snapshot?.revision) || 0);
  }

  function syncSnapshotState(snapshot, opts = {}) {
    const revision = getSnapshotRevision(snapshot);
    if (revision > 0) {
      latestCartRevision = Math.max(latestCartRevision, revision);
      if (opts.updateSeenRevision) {
        lastSeenRevision = Math.max(lastSeenRevision, revision);
      }
    }
    if (!snapshot?.cart) return false;
    latestCartSnapshot = snapshot.cart;
    syncRootCartState(getMembersRoot(), snapshot.cart);
    return true;
  }

  function hasUsableCartSnapshot(cart) {
    return !!cart && Array.isArray(cart.items);
  }

  function isSettlementIdle() {
    return getSettlementCoordinator()?.isBusy !== true;
  }

  function canUseCurrentSnapshot(cart) {
    return hasUsableCartSnapshot(cart) && latestCartRevision === lastSeenRevision && isSettlementIdle();
  }

  async function waitForStableCart(timeoutMs = 1800) {
    const reconciler = getReconciler();
    if (!reconciler?.whenIdle) return true;

    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
      await reconciler.whenIdle();
      return true;
    }

    let timeoutId = null;
    try {
      const result = await Promise.race([
        reconciler.whenIdle().then(() => true),
        new Promise((resolve) => {
          timeoutId = setTimeout(() => resolve(false), timeoutMs);
        }),
      ]);
      return result;
    } finally {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    }
  }

  async function fetchCart() {
    const cartStore = getCartStore();
    const storeSnapshot = cartStore?.getSnapshot?.();
    syncSnapshotState(storeSnapshot);

    const transport = getTransport();
    const cart = transport?.fetchCart
      ? await transport.fetchCart()
      : await fetch('/cart.js', { credentials: 'same-origin' }).then(async (response) => {
          if (!response.ok) throw new Error('cart_http_' + response.status);
          return response.json();
        });

    cartStore?.replaceCart?.(cart, {
      source: 'dr-members',
      reason: 'members-refresh',
      replaceSections: false,
    });
    const nextSnapshot = cartStore?.getSnapshot?.();
    if (syncSnapshotState(nextSnapshot, { updateSeenRevision: true })) {
      return latestCartSnapshot;
    }
    latestCartSnapshot = cart;
    syncRootCartState(getMembersRoot(), cart);
    return cart;
  }

  function extractActiveDiscountCodes(cart) {
    const codes = new Set();
    const addCode = (value) => {
      const code = String(value || '').trim().toUpperCase();
      if (code) codes.add(code);
    };
    // /cart.js lists these flat; /cart/change.js and /cart/update.js wrap them
    // in { amount, discount_application }.
    (cart?.cart_level_discount_applications || cart?.discount_applications || []).forEach((discount) => {
      const app = discount?.discount_application || discount;
      addCode(app?.code || app?.title);
    });
    (cart?.items || []).forEach((item) => {
      (item?.discounts || []).forEach((discount) => addCode(discount?.code || discount?.title));
      (item?.line_level_discount_allocations || []).forEach((alloc) => {
        const app = alloc?.discount_application || alloc;
        addCode(app?.code || app?.title);
      });
      (item?.discount_allocations || []).forEach((alloc) => {
        const app = alloc?.discount_application || alloc;
        addCode(app?.code || app?.title);
      });
    });
    return codes;
  }

  async function fetchJsonWithTimeout(url, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { method: 'GET', credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('members_http_' + response.status);
      return response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  function requestRefresh(opts = {}) {
    if (opts.force) {
      forceRefreshPending = true;
    }
    refreshEpoch += 1;
    refreshQueued = true;
    if (refreshInFlight) return;
    runRefreshLoop();
  }

  async function runRefreshLoop() {
    if (refreshInFlight || !refreshQueued) return;
    refreshInFlight = true;
    const epoch = refreshEpoch;
    refreshQueued = false;
    try {
      await refreshMembersState(epoch);
    } catch (err) {
      console.error('[DR Members] Refresh failed:', err);
    } finally {
      refreshInFlight = false;
      if (refreshQueued) runRefreshLoop();
    }
  }

  async function refreshMembersState(epoch) {
    const config = getConfig();
    const root = getMembersRoot();
    if (!config || !root) {
      pushDebug('members-refresh-skip', summarizeRefresh({
        epoch,
        reason: !config ? 'no_config' : 'no_root',
      }));
      return;
    }
    const rootId = getRootDebugId(root);
    if (epoch !== refreshEpoch) {
      pushDebug('members-refresh-skip', summarizeRefresh({
        epoch,
        rootId,
        reason: 'stale_epoch_before_start',
      }));
      return;
    }

    const rootState = readRootState(root, latestCartSnapshot);
    const els = getStateElements(root);
    const minPointsForCoupon = normalizeMinPoints(config.minPointsForCoupon);
    pushDebug('members-refresh-start', summarizeRefresh({
      epoch,
      ...summarizeRoot(root, rootState),
    }));
    
    if (!rootState.loggedIn) {
      pushDebug('members-refresh-skip', summarizeRefresh({
        epoch,
        reason: 'logged_out',
        ...summarizeRoot(root, rootState),
      }));
      forceRefreshPending = false;
      fillSelect(els.existingSelect, [], 'code', 'בחר/י קופון קיים...');
      fillSelect(els.creatableSelect, [], 'id', 'המר/י נקודות לקופון...');
      els.existingWrap?.setAttribute('hidden', '');
      els.creatableWrap?.setAttribute('hidden', '');
      syncMinPointsMessage(els, minPointsForCoupon, false);
      showState(els, 'loggedOut');
      return;
    }
    if (!rootState.mainActive && !rootState.exclusiveActive) {
      pushDebug('members-refresh-skip', summarizeRefresh({
        epoch,
        reason: 'main_coupon_inactive',
        ...summarizeRoot(root, rootState),
      }));
      forceRefreshPending = false;
      fillSelect(els.existingSelect, [], 'code', 'בחר/י קופון קיים...');
      fillSelect(els.creatableSelect, [], 'id', 'המר/י נקודות לקופון...');
      els.existingWrap?.setAttribute('hidden', '');
      els.creatableWrap?.setAttribute('hidden', '');
      if (els.existingSelect) els.existingSelect.value = '';
      if (els.creatableSelect) els.creatableSelect.value = '';
      if (els.applyExisting) els.applyExisting.disabled = true;
      if (els.applyCreatable) els.applyCreatable.disabled = true;
      syncMinPointsMessage(els, minPointsForCoupon, false);
      showState(els, 'activateMain');
      return;
    }
    const forceRefresh = forceRefreshPending;
    const hydrationIsCurrent = !!hydratedFor &&
      hydratedFor.root === root &&
      root.isConnected &&
      hydratedFor.revision >= lastSeenRevision;
    if (!forceRefresh && hydrationIsCurrent) {
      pushDebug('members-refresh-freshness-skip', summarizeRefresh({
        epoch,
        rootStillCurrent: isCurrentRoot(root),
        ...summarizeRoot(root, rootState),
      }));
      if (els.loading && !els.loading.hasAttribute('hidden')) {
        showState(els, 'eligible');
      }
      return;
    }

    // Keep the controls visible during background refreshes to avoid
    // native select popups collapsing when state swaps to skeleton/loading.
    const silentRefresh = !!els.eligible && !els.eligible.hasAttribute('hidden');
    if (!silentRefresh) {
      showState(els, 'loading');
    }
    const loadingStartedAt = Date.now();
    let cart = latestCartSnapshot;
    if (!canUseCurrentSnapshot(cart)) {
      const hadSnapshot = hasUsableCartSnapshot(cart);
      const settled = await waitForStableCart(hadSnapshot ? 900 : 1800);
      if (epoch !== refreshEpoch) {
        pushDebug('members-refresh-abort', summarizeRefresh({
          epoch,
          reason: 'stale_epoch_after_wait',
          rootStillCurrent: isCurrentRoot(root),
          ...summarizeRoot(root),
        }));
        return;
      }
      if (!isCurrentRoot(root)) {
        scheduleStaleRootRefresh('stale_root_after_wait', epoch, root);
        return;
      }
      const settledSnapshot = getCartStore()?.getSnapshot?.();
      syncSnapshotState(settledSnapshot, { updateSeenRevision: true });
      cart = latestCartSnapshot;
      if (!hasUsableCartSnapshot(cart) || settled === false) {
        if (settled === false) {
          console.warn('[DR Members] Settlement wait timed out, falling back to cart fetch.');
        }
        cart = await fetchCart();
        if (epoch !== refreshEpoch) {
          pushDebug('members-refresh-abort', summarizeRefresh({
            epoch,
            reason: 'stale_epoch_after_cart_fetch',
            rootStillCurrent: isCurrentRoot(root),
            ...summarizeRoot(root),
          }));
          return;
        }
        if (!isCurrentRoot(root)) {
          scheduleStaleRootRefresh('stale_root_after_cart_fetch', epoch, root);
          return;
        }
      }
    } else {
      syncRootCartState(root, cart);
    }
    latestCartSnapshot = cart;

    const url = new URL(config.optionsEndpoint, window.location.origin);
    if (rootState.customerEmail) url.searchParams.set('customer_email', rootState.customerEmail);
    if (rootState.customerId) url.searchParams.set('customer_id', rootState.customerId);
    url.searchParams.set('cart_total_agorot', String(cart.total_price || rootState.cartTotalAgorot || 0));
    // Conversion choices are points-driven. Request all affordable choices and
    // let Shopify determine cart applicability when the redeemed code is applied.
    url.searchParams.set('debug_all', '1');

    try {
      const data = await fetchJsonWithTimeout(url.toString(), config.timeoutMs);
      if (epoch !== refreshEpoch) {
        pushDebug('members-refresh-abort', summarizeRefresh({
          epoch,
          reason: 'stale_epoch_after_options',
          rootStillCurrent: isCurrentRoot(root),
          ...summarizeRoot(root),
        }));
        return;
      }
      if (!isCurrentRoot(root)) {
        scheduleStaleRootRefresh('stale_root_after_options', epoch, root);
        return;
      }
      const activeCodes = extractActiveDiscountCodes(cart);
      const allExistingCoupons = normalizeExistingCoupons(
        data?.existingCoupons || data?.existing || data?.existing_coupons
      );
      const existingCoupons = allExistingCoupons.filter((item) => !activeCodes.has(String(item?.code || '').toUpperCase()));
      const creatableNow = normalizeCreatableOptions(
        data?.creatableNow || data?.creatable || data?.creatable_options
      );

      fillSelect(els.existingSelect, existingCoupons, 'code', 'בחר/י קופון קיים...', { disableNonApplicable: true });
      fillSelect(els.creatableSelect, creatableNow, 'id', 'המר/י נקודות לקופון...');

      const hasExisting = existingCoupons.length > 0;
      const hasCreatable = creatableNow.length > 0;
      if (hasExisting) els.existingWrap?.removeAttribute('hidden');
      else els.existingWrap?.setAttribute('hidden', '');
      if (hasCreatable) els.creatableWrap?.removeAttribute('hidden');
      else els.creatableWrap?.setAttribute('hidden', '');
      syncMinPointsMessage(els, minPointsForCoupon, !hasExisting && !hasCreatable);

      if (els.existingSelect) els.existingSelect.value = '';
      if (els.applyExisting) els.applyExisting.disabled = true;
      if (els.applyCreatable) {
        const selectedCreatable = String(els.creatableSelect?.value || '').trim();
        els.applyCreatable.disabled = !selectedCreatable;
      }

      const availablePoints = Number(
        data?.availablePoints ?? data?.available_points ?? data?.pointsBalance ?? data?.points_balance
      );
      const fallbackPoints = Math.max(0, Math.floor((Number(cart.total_price || 0) / 100) * config.pointsRatio));
      const shownPoints = Number.isFinite(availablePoints) ? availablePoints : fallbackPoints;
      setMembersPointsText(els.points, shownPoints);
      renderClubCarousel(root, {
        coupons: allExistingCoupons,
        activeCodes,
        creatable: creatableNow,
        points: shownPoints,
      });
      if (!silentRefresh) {
        if (!(await ensureMinLoadingWindow(loadingStartedAt, epoch))) {
          pushDebug('members-refresh-abort', summarizeRefresh({
            epoch,
            reason: 'stale_epoch_after_min_loading_success',
            rootStillCurrent: isCurrentRoot(root),
            ...summarizeRoot(root),
          }));
          return;
        }
        if (!isCurrentRoot(root)) {
          scheduleStaleRootRefresh('stale_root_after_min_loading', epoch, root);
          return;
        }
        showState(els, 'eligible');
      }
      if (!isCurrentRoot(root)) {
        scheduleStaleRootRefresh('stale_root_before_success', epoch, root);
        return;
      }
      forceRefreshPending = false;
      hydratedFor = { root, revision: lastSeenRevision };
      pushDebug('members-refresh-success', summarizeRefresh({
        epoch,
        rootStillCurrent: isCurrentRoot(root),
        ...summarizeRoot(root),
      }));
    } catch (err) {
      console.error('[DR Members] Options fetch failed:', err);
      if (epoch !== refreshEpoch) {
        pushDebug('members-refresh-abort', summarizeRefresh({
          epoch,
          reason: 'stale_epoch_after_options_error',
          rootStillCurrent: isCurrentRoot(root),
          ...summarizeRoot(root),
        }));
        return;
      }
      pushDebug('members-refresh-abort', summarizeRefresh({
        epoch,
        reason: 'options_error',
        error: String(err?.message || err),
        rootStillCurrent: isCurrentRoot(root),
        ...summarizeRoot(root),
      }));
      if (!isCurrentRoot(root)) {
        scheduleStaleRootRefresh('stale_root_after_options_error', epoch, root);
        return;
      }
      setMembersPointsText(els.points, 0);
      const wrapsHidden =
        !!els.existingWrap?.hasAttribute('hidden') &&
        !!els.creatableWrap?.hasAttribute('hidden');
      syncMinPointsMessage(els, minPointsForCoupon, wrapsHidden);
      if (!silentRefresh) {
        if (!(await ensureMinLoadingWindow(loadingStartedAt, epoch))) {
          pushDebug('members-refresh-abort', summarizeRefresh({
            epoch,
            reason: 'stale_epoch_after_min_loading_error',
            rootStillCurrent: isCurrentRoot(root),
            ...summarizeRoot(root),
          }));
          return;
        }
        if (!isCurrentRoot(root)) {
          scheduleStaleRootRefresh('stale_root_after_min_loading_error', epoch, root);
          return;
        }
        showState(els, 'eligible');
      }
      if (!isCurrentRoot(root)) {
        scheduleStaleRootRefresh('stale_root_before_error_state', epoch, root);
        return;
      }
      forceRefreshPending = false;
    }
  }

  function markDirty(opts = {}) {
    requestRefresh(opts);
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    if (loading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function queueCreatableAction(task) {
    const nextTask = creatableActionQueue.catch(() => {}).then(() => task());
    creatableActionQueue = nextTask.catch(() => {});
    return nextTask;
  }

  function hideInapplicableTooltip(root = getMembersRoot()) {
    if (inapplicableTooltipTimer !== null) {
      clearTimeout(inapplicableTooltipTimer);
      inapplicableTooltipTimer = null;
    }
    const currentRoot = root?.isConnected ? root : getMembersRoot();
    if (!currentRoot) return;
    const currentEls = getStateElements(currentRoot);
    currentEls.inapplicableTooltip?.setAttribute('hidden', '');
    currentEls.applyCreatable?.removeAttribute('aria-describedby');
  }

  function showInapplicableTooltip(root = getMembersRoot(), text = '') {
    hideInapplicableTooltip(root);
    const currentRoot = root?.isConnected ? root : getMembersRoot();
    if (!currentRoot) return;
    const currentEls = getStateElements(currentRoot);
    const tooltip = currentEls.inapplicableTooltip;
    if (!tooltip) return;
    if (!tooltip.dataset.defaultText) tooltip.dataset.defaultText = tooltip.textContent.trim();
    tooltip.textContent = text || tooltip.dataset.defaultText;
    tooltip.removeAttribute('hidden');
    if (tooltip.id) {
      currentEls.applyCreatable?.setAttribute('aria-describedby', tooltip.id);
    }
    inapplicableTooltipTimer = setTimeout(() => {
      hideInapplicableTooltip();
    }, INAPPLICABLE_TOOLTIP_HIDE_MS);
  }

  function upsertDisabledExistingCoupon(els, couponCode) {
    const normalizedCode = String(couponCode || '').trim().toUpperCase();
    if (!els?.existingSelect || !normalizedCode) return;
    const existingOption = Array.from(els.existingSelect.options || []).find(
      (option) => String(option.value || '').trim().toUpperCase() === normalizedCode
    );
    const option = existingOption || document.createElement('option');
    option.value = normalizedCode;
    option.textContent = normalizedCode + ' (לא זמין כרגע)';
    option.disabled = true;
    if (!existingOption) {
      els.existingSelect.appendChild(option);
    }
    els.existingWrap?.removeAttribute('hidden');
    els.existingSelect.value = '';
    if (els.applyExisting) els.applyExisting.disabled = true;
  }

  async function handleApplyExisting(root, els) {
    const code = String(els.existingSelect?.value || '').trim().toUpperCase();
    if (!code) return;
    const selectedOption = els.existingSelect?.selectedOptions?.[0];
    if (selectedOption?.disabled) return;
    const reconciler = getReconciler();
    if (!reconciler?.applyDiscountCode) return;
    setButtonLoading(els.applyExisting, true);
    if (reconciler.requestMembersReopen) {
      reconciler.requestMembersReopen({ activeTab: 'members', membersOpen: true });
    }
    writeUiState({ activeTab: 'members', membersOpen: true });
    try {
      await reconciler.applyDiscountCode(code, { mode: 'append' });
    } catch (err) {
      console.error('[DR Members] Existing apply failed:', err);
    } finally {
      setButtonLoading(els.applyExisting, false);
    }
  }

  async function handleApplyCreatable(root, els, config) {
    const selectedId = String(els.creatableSelect?.value || '').trim();
    if (!selectedId || creatableActionInFlight) return;
    creatableActionInFlight = true;
    hideInapplicableTooltip(root);
    setButtonLoading(els.applyCreatable, true);
    return queueCreatableAction(async () => {
      const rootState = readRootState(root);
      let refreshOptions = false;
      try {
        const response = await fetch(config.redeemEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            optionId: selectedId,
            customerId: rootState.customerId,
            customerEmail: rootState.customerEmail,
            cartTotalAgorot: rootState.cartTotalAgorot,
          }),
        });
        if (!response.ok) throw new Error('redeem_http_' + response.status);
        refreshOptions = true;

        const redeemPayload = await response.json().catch(() => null);
        const redeemedCode = String(redeemPayload?.code || '').trim().toUpperCase();
        if (!redeemedCode) {
          console.error('[DR Members] Creatable redeem missing code response');
          return;
        }

        const reconciler = getReconciler();
        if (!reconciler?.applyDiscountCode) {
          console.error('[DR Members] Converted coupon apply unavailable');
          return;
        }
        if (reconciler.requestMembersReopen) {
          reconciler.requestMembersReopen({ activeTab: 'members', membersOpen: true });
        }
        writeUiState({ activeTab: 'members', membersOpen: true });

        let applyResult;
        try {
          applyResult = await reconciler.applyDiscountCode(redeemedCode, { mode: 'append' });
        } catch (err) {
          console.error('[DR Members] Converted coupon apply failed:', err);
          return;
        }

        if (applyResult?.success === false) {
          const currentRoot = getMembersRoot();
          const currentEls = currentRoot ? getStateElements(currentRoot) : null;
          if (currentRoot && currentEls) {
            upsertDisabledExistingCoupon(currentEls, redeemedCode);
            showInapplicableTooltip(currentRoot);
          }
        }
      } catch (err) {
        console.error('[DR Members] Creatable redeem failed:', err);
      } finally {
        if (refreshOptions) markDirty({ force: true });
        creatableActionInFlight = false;
        setButtonLoading(els.applyCreatable, false);
        const currentRoot = getMembersRoot();
        if (currentRoot && currentRoot !== root) {
          const currentEls = getStateElements(currentRoot);
          if (currentEls.applyCreatable) {
            currentEls.applyCreatable.removeAttribute('aria-busy');
            currentEls.applyCreatable.disabled = !String(currentEls.creatableSelect?.value || '').trim();
          }
        }
      }
    });
  }

  // --- Compact layout: coupon carousel and conversion sheet -----------------

  function formatShekelsFromAgorot(agorot) {
    return '₪' + Math.ceil(Number(agorot) / 100);
  }

  function makeEl(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  }

  function buildCouponChip(coupon, applied) {
    const chip = makeEl('button', 'dr-gifts-c__cp' + (applied ? ' is-on' : ''));
    chip.type = 'button';
    chip.setAttribute('data-dr-club-coupon', coupon.code);
    chip.setAttribute('aria-pressed', String(applied));
    const hasMin = Number.isFinite(coupon.minCartAgorot) && coupon.minCartAgorot > 0;
    const minText = hasMin ? 'מעל ' + formatShekelsFromAgorot(coupon.minCartAgorot) : '';
    chip.disabled = !applied && coupon.applicable === false;
    chip.append(
      makeEl('span', 'dr-gifts-c__cp-amount', coupon.amountText || coupon.label),
      makeEl('span', 'dr-gifts-c__cp-code', coupon.code),
      makeEl('span', 'dr-gifts-c__cp-min', applied ? 'הוחל · להסרה' : minText || 'להחלה')
    );
    const state = applied ? 'מוחל. לחצי להסרה' : chip.disabled ? 'זמין בסל ' + minText : 'לחצי להחלה';
    chip.setAttribute('aria-label', [coupon.amountText || coupon.label, 'קוד ' + coupon.code, state].join(', '));
    return chip;
  }

  function buildConvertChip(points) {
    const chip = makeEl('button', 'dr-gifts-c__cp dr-gifts-c__cp--add');
    chip.type = 'button';
    chip.setAttribute('data-dr-sheet-open', 'convert');
    chip.setAttribute('aria-haspopup', 'dialog');
    chip.append(
      makeEl('span', 'dr-gifts-c__cp-plus', '+'),
      makeEl('span', 'dr-gifts-c__cp-label', 'המרת נקודות'),
      makeEl('span', 'dr-gifts-c__cp-min', String(points) + ' נק׳')
    );
    return chip;
  }

  function buildConvertOption(option, index) {
    const button = makeEl('button', 'dr-gifts-c__convert-opt');
    button.type = 'button';
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');
    button.tabIndex = index === 0 ? 0 : -1;
    button.setAttribute('data-dr-sheet-option', option.id);
    const label = makeEl('span', 'dr-gifts-c__convert-label', option.baseLabel || option.label);
    const hasMin = Number.isFinite(option.minCartAgorot) && option.minCartAgorot > 0;
    if (option.applicable === false) {
      const later = 'לפעם הבאה' + (hasMin ? ': בסל מעל ' + formatShekelsFromAgorot(option.minCartAgorot) : '');
      label.append(makeEl('span', 'dr-gifts-c__convert-min is-later', later));
    } else if (hasMin) {
      label.append(makeEl('span', 'dr-gifts-c__convert-min', 'בסל מעל ' + formatShekelsFromAgorot(option.minCartAgorot)));
    }
    button.append(label);
    return button;
  }

  function renderClubCarousel(root, data) {
    const carousel = root.querySelector('[data-dr-members-carousel]');
    if (!carousel) return;
    const coupons = Array.isArray(data.coupons) ? data.coupons : [];
    const creatable = Array.isArray(data.creatable) ? data.creatable : [];
    const activeCodes = data.activeCodes || new Set();
    const chips = [];
    if (creatable.length > 0) chips.push(buildConvertChip(data.points));
    coupons
      .slice()
      .sort((a, b) => Number(activeCodes.has(b.code)) - Number(activeCodes.has(a.code)))
      .forEach((coupon) => chips.push(buildCouponChip(coupon, activeCodes.has(coupon.code))));
    carousel.replaceChildren(...chips);
    carousel.toggleAttribute('hidden', chips.length === 0);

    const hint = root.querySelector('[data-dr-members-hint]');
    if (hint) {
      hint.textContent =
        coupons.length > 0
          ? 'לחצי על קופון כדי להחיל אותו. אפשר כמה יחד.'
          : creatable.length > 0
          ? 'המירי נקודות לקופון והחילי אותו על הסל.'
          : '';
    }

    const widget = root.closest('[data-dr-gifts-widget]');
    const options = widget?.querySelector('[data-dr-convert-options]');
    if (options) options.replaceChildren(...creatable.map(buildConvertOption));
    const pointsLabel = widget?.querySelector('[data-dr-convert-points]');
    if (pointsLabel) pointsLabel.textContent = String(data.points);
    const go = widget?.querySelector('[data-dr-convert-go]');
    if (go) go.disabled = true;
  }

  async function handleCouponChip(chip) {
    const code = String(chip.getAttribute('data-dr-club-coupon') || '').trim().toUpperCase();
    const reconciler = getReconciler();
    if (!code || !reconciler?.applyDiscountCode || chip.disabled || chip.getAttribute('aria-busy') === 'true') return;
    chip.setAttribute('aria-busy', 'true');
    if (reconciler.requestMembersReopen) {
      reconciler.requestMembersReopen({ activeTab: 'members', membersOpen: true });
    }
    writeUiState({ activeTab: 'members', membersOpen: true });
    hideInapplicableTooltip();
    try {
      if (chip.getAttribute('aria-pressed') === 'true') {
        await reconciler.removeDiscountCode(code);
      } else {
        const result = await reconciler.applyDiscountCode(code, { mode: 'append' });
        // Shopify's discount combinations decide what stacks (DRNEW with club
        // coupons, say); when it doesn't apply the coupon, say so.
        if (result?.success === false) showInapplicableTooltip(getMembersRoot(), COUPON_NOT_COMBINED_TEXT);
      }
    } catch (err) {
      console.error('[DR Members] Coupon chip failed:', err);
    } finally {
      chip.removeAttribute('aria-busy');
      markDirty({ force: true });
    }
  }

  async function handleConvertGo(button, root, els, config) {
    const sheet = button.closest('[data-dr-sheet]');
    const selected = sheet?.querySelector('[data-dr-sheet-option][aria-checked="true"]');
    const optionId = String(selected?.getAttribute('data-dr-sheet-option') || '').trim();
    if (!optionId || !els.creatableSelect || button.getAttribute('aria-busy') === 'true') return;
    els.creatableSelect.value = optionId;
    if (els.creatableSelect.value !== optionId) return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      await handleApplyCreatable(root, els, config);
    } finally {
      window.drCartGiftsSheets?.close({ restoreFocus: false });
    }
  }

  function buildLoginUrl(baseLoginUrl) {
    const returnPath = window.location.pathname + window.location.search + window.location.hash;
    const loginUrl = new URL(baseLoginUrl || '/customer_authentication/login', window.location.origin);
    // Different auth setups use different return params; set both for compatibility.
    loginUrl.searchParams.set('return_to', returnPath);
    loginUrl.searchParams.set('return_url', returnPath);
    return loginUrl.toString();
  }

  function openDrawerAndRestoreMembersTab() {
    const trigger = document.querySelector('#cart-icon-bubble');

    function restoreUi() {
      requestAnimationFrame(() => {
        setActiveTab('members', { membersOpen: true });
        markDirty();
      });
    }

    function openDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(trigger || undefined);
      } else if (trigger) {
        trigger.click();
      }
      restoreUi();
    }

    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer').then(openDrawer).catch(openDrawer);
    } else {
      openDrawer();
    }
  }

  function setupDrawerObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer === observedDrawer) return;
    if (drawerObserver) drawerObserver.disconnect();
    observedDrawer = drawer;
    drawerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') continue;
        if (drawer.classList.contains('active') && readUiState().activeTab === 'members') {
          requestRefresh();
        }
      }
    });
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function bindEvents() {
    document.addEventListener('click', (event) => {
      const tabButton = event.target.closest('[data-dr-tab]');
      if (tabButton) {
        const nextTab = tabButton.getAttribute('data-dr-tab');
        if (tabButton.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        if (nextTab === 'members' && !isModuleEnabled('membersTab')) {
          event.preventDefault();
          return;
        }
        if (nextTab === 'new-customer' && !isModuleEnabled('newMemberTab')) {
          event.preventDefault();
          return;
        }
        setActiveTab(nextTab, { membersOpen: nextTab === 'members' });
        if (nextTab === 'members') {
          primeMembersLoadingState();
          markDirty();
        }
      }

      const root = getMembersRoot();
      if (!root) return;
      const config = getConfig();
      const els = getStateElements(root);
      const state = readRootState(root, latestCartSnapshot);

      if (event.target.closest('[data-dr-members-apply-existing]')) {
        event.preventDefault();
        event.stopPropagation();
        handleApplyExisting(root, els);
      }

      if (event.target.closest('[data-dr-members-apply-creatable]')) {
        event.preventDefault();
        event.stopPropagation();
        if (config) handleApplyCreatable(root, els, config);
      }

      const couponChip = event.target.closest('[data-dr-club-coupon]');
      if (couponChip) {
        event.preventDefault();
        handleCouponChip(couponChip);
      }

      const convertGo = event.target.closest('[data-dr-convert-go]');
      if (convertGo && config) {
        event.preventDefault();
        handleConvertGo(convertGo, root, els, config);
      }

      const loginLink = event.target.closest('.dr-gifts__members-login-link');
      if (loginLink) {
        writeUiState({ activeTab: 'members', membersOpen: true, returnPending: true });
        loginLink.href = buildLoginUrl(state.loginUrl);
      }
    }, true);

    document.addEventListener('change', (event) => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement)) return;
      if (select.matches('[data-dr-members-existing-select]')) {
        const root = getMembersRoot();
        if (!root) return;
        const els = getStateElements(root);
        const selectedOption = select.selectedOptions?.[0];
        if (els.applyExisting) {
          els.applyExisting.disabled = !selectedOption || selectedOption.disabled || !String(select.value || '').trim();
        }
      }
      if (select.matches('[data-dr-members-creatable-select]')) {
        const root = getMembersRoot();
        if (!root) return;
        const els = getStateElements(root);
        hideInapplicableTooltip(root);
        if (els.applyCreatable) {
          els.applyCreatable.disabled = !String(select.value || '').trim();
        }
      }
    }, true);

    document.addEventListener(MEMBERS_RERENDER_EVENT, (event) => {
      if (event?.detail?.activeTab !== 'members') return;
      hideInapplicableTooltip();
      primeMembersLoadingState();
      requestRefresh({ force: true });
    });

    const cartStore = getCartStore();
    if (cartStore?.subscribe) {
      cartStore.subscribe((snapshot) => {
        syncSnapshotState(snapshot, { updateSeenRevision: true });
        if (readUiState().activeTab === 'members') {
          markDirty();
        }
      });
    } else if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData) latestCartSnapshot = event.cartData;
        // Without cart-store revisions, force a refresh so the freshness gate
        // cannot permanently skip updates on legacy pub/sub-only pages.
        if (readUiState().activeTab === 'members') {
          markDirty({ force: true });
        }
      });
    }
  }

  function restoreOnInit() {
    const state = readUiState();
    if (state.activeTab === 'members' || state.activeTab === 'new-customer') {
      setActiveTab(state.activeTab, { membersOpen: state.activeTab === 'members' });
    }
    const root = getMembersRoot();
    if (state.returnPending) {
      writeUiState({ returnPending: false, activeTab: 'members', membersOpen: true });
      openDrawerAndRestoreMembersTab();
      return;
    }
    if (!root) return;
  }

  function init() {
    if (!getConfig() || !isModuleEnabled('membersTab')) return;
    const cartStore = getCartStore();
    const initialSnapshot = cartStore?.getSnapshot?.();
    syncSnapshotState(initialSnapshot, { updateSeenRevision: true });
    bindEvents();
    setupDrawerObserver();
    restoreOnInit();
    if (readUiState().activeTab === 'members') {
      requestRefresh();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
